import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Checkbox,
  Box,
  Chip,
  Avatar,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  School as SchoolIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { StudentSearchResult } from '../../types/student-search';
import { getPrefectureName } from '../../constants/prefectures';

interface StudentCardProps {
  student: StudentSearchResult;
  selected: boolean;
  onSelect: () => void;
  onViewDetails: () => void;
  onSendScout?: () => void;
}

// 学習活動スコアの表示
const getLearningActivityDisplay = (
  score: number
): { label: string; color: 'success' | 'warning' | 'error' | 'default' } => {
  if (score >= 80) {
    return { label: '非常に活発', color: 'success' };
  } else if (score >= 60) {
    return { label: '活発', color: 'success' };
  } else if (score >= 40) {
    return { label: '普通', color: 'warning' };
  } else if (score >= 20) {
    return { label: '低活動', color: 'error' };
  } else {
    return { label: '非活動', color: 'error' };
  }
};

// 日付フォーマット
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// 学習スコアからレベルを判定
const getLearningLevelFromScore = (
  score: number
): { level: 'Low' | 'Medium' | 'High'; label: string } => {
  if (score >= 70) {
    return { level: 'High', label: '高い' };
  } else if (score >= 40) {
    return { level: 'Medium', label: '普通' };
  } else {
    return { level: 'Low', label: '低い' };
  }
};

// MVPスコアレベルのラベル表示（互換性のため残す）
const getLearningLevelLabel = (level: 'Low' | 'Medium' | 'High'): string => {
  switch (level) {
    case 'High':
      return '高い';
    case 'Medium':
      return '普通';
    case 'Low':
      return '低い';
    default:
      return '不明';
  }
};

// MVPスコアレベルの色
const getLearningLevelColor = (
  level: 'Low' | 'Medium' | 'High'
): 'success' | 'warning' | 'error' => {
  switch (level) {
    case 'High':
      return 'success';
    case 'Medium':
      return 'warning';
    case 'Low':
      return 'error';
    default:
      return 'error';
  }
};

export const StudentCard: React.FC<StudentCardProps> = ({
  student,
  selected,
  onSelect,
  onViewDetails,
  onSendScout,
}) => {
  const learningActivity = getLearningActivityDisplay(
    Math.min(student.activity.learning_activity_score, 100)
  );

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease-in-out',
        border: selected ? 2 : 1,
        borderColor: selected ? 'primary.main' : 'divider',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
        // レスポンシブ対応
        minHeight: { xs: 'auto', sm: '400px' },
      }}
    >
      {/* カードヘッダー */}
      <Box sx={{ p: { xs: 1.5, sm: 2 }, pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.5 },
              flex: 1,
              minWidth: 0, // テキストの省略を有効にする
            }}
          >
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 },
                fontSize: { xs: '1rem', sm: '1.25rem' },
              }}
            >
              {student.full_name.charAt(0)}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {student.full_name}
              </Typography>
              {(student.age || student.graduation_year) && (
                <Typography variant="body2" color="text.secondary">
                  {student.age && `${student.age}歳`}
                  {student.age && student.graduation_year && ' / '}
                  {student.graduation_year && `${student.graduation_year}年卒`}
                </Typography>
              )}
            </Box>
          </Box>
          <Checkbox
            checked={selected}
            onChange={onSelect}
            size="small"
            sx={{
              mt: { xs: 0, sm: -1 },
              mr: { xs: 0, sm: -1 },
              flexShrink: 0,
            }}
          />
        </Box>
      </Box>

      <CardContent
        sx={{
          flexGrow: 1,
          pt: 0,
          p: { xs: 1.5, sm: 2 },
          '&:last-child': { pb: { xs: 1.5, sm: 2 } },
        }}
      >
        {/* 基本情報 */}
        <Box sx={{ mb: 2 }}>
          {student.university && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
            >
              <SchoolIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {student.university}
                {student.faculty && ` ${student.faculty}`}
                {student.grade && ` ${student.grade}年`}
              </Typography>
            </Box>
          )}

          {student.current_prefecture && (
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
            >
              <LocationIcon fontSize="small" color="action" />
              <Typography variant="body2">
                {getPrefectureName(student.current_prefecture) ||
                  student.current_prefecture}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <CalendarIcon fontSize="small" color="action" />
            <Typography variant="body2">
              最終活動: {formatDate(student.activity.last_active_date)}
            </Typography>
          </Box>
        </Box>

        {/* 自己PR・志望動機（抜粋） */}
        {student.bio && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              自己PR:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {student.bio}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* 活動データ */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {/* 統合学習スコア表示 */}
          {student.activity.learning_score && (
            <Chip
              label={`学習スコア: ${student.activity.learning_score.total}/100 (${getLearningLevelFromScore(student.activity.learning_score.total).label})`}
              color={getLearningLevelColor(
                getLearningLevelFromScore(student.activity.learning_score.total)
                  .level
              )}
              size="small"
              variant="filled"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                height: { xs: 24, sm: 'auto' },
              }}
            />
          )}
        </Box>

        {/* 学習期間 */}
        {student.activity.learning_period && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              学習期間:{' '}
              {formatDate(student.activity.learning_period.start_date || '')} 〜
              {student.activity.learning_period.end_date
                ? formatDate(student.activity.learning_period.end_date)
                : '継続中'}
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* カードアクション */}
      <CardActions
        sx={{
          p: { xs: 1.5, sm: 2 },
          pt: 0,
          display: 'flex',
          gap: { xs: 0.5, sm: 1 },
          alignItems: 'stretch',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'stretch',
        }}
      >
        <Button
          variant="outlined"
          startIcon={<VisibilityIcon />}
          onClick={onViewDetails}
          size="small"
          fullWidth
          sx={{ flex: 1 }}
        >
          詳細表示
        </Button>
        {onSendScout && (
          <Tooltip
            title={
              student.accepts_scout_emails
                ? 'スカウトメールを送信できます'
                : 'この学生はスカウトメールの受信を停止しています'
            }
            disableHoverListener={student.accepts_scout_emails}
            arrow
          >
            <span style={{ flex: 1, display: 'flex' }}>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={onSendScout}
                size="small"
                fullWidth
                sx={{ flex: 1 }}
                disabled={!student.accepts_scout_emails}
              >
                スカウト
              </Button>
            </span>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );
};
